import { api, call } from '../platform/api.js';
import type { OptionManage } from './option-sheet.js';

/**
 * THE ＋ ✎ ✕ TOOLS FOR CATEGORIES AND LOCATIONS, IN ONE PLACE.
 *
 * ⚠️ EXTRACTED BEFORE THE THIRD COPY EXISTED, 31 Jul 2026 — deliberately unlike the admin
 * gate in `backup-route.ts`, which is now written out three times and carries a comment
 * apologising for it. The atelier asked for these tools on the intake screen and in the
 * bulk-edit sheet as well as the edit screen, and each block is some sixty lines of
 * mutation-plus-blast-radius wording. Copied twice more, a fix to the blast-radius text or
 * the follow-the-rename rule would land in one screen and quietly not in the others.
 *
 * WHAT A CALLER STILL OWNS, because it genuinely differs per screen:
 *  - `selected` / `onSelected`: the edit screen holds one item's category and must follow a
 *    rename or it would save a path that no longer exists; intake holds a carried-over
 *    context; the bulk sheet holds a pending change. Same rule, three different homes.
 *  - `usage`: the blast-radius counts. A screen that has not loaded them says so rather
 *    than claiming "no item uses it" — Guardrail 9, since that reassurance would be
 *    invented and the act it precedes is a silent blanking.
 */

/** What `structureUsage` answers with. Structural, so a screen can hold it in its own state. */
export interface StructureUsage {
  categories: { path: string; itemCount: number; childCount: number }[];
  locations: { name: string; itemCount: number }[];
  brands: { name: string; itemCount: number }[];
}

/** Everything the mutations need from the screen around them. */
interface Wiring<T> {
  /** Fresh catalogue meta after every mutation — the picker re-renders from the server's copy. */
  setMeta: (meta: T) => void;
  /** Re-read the blast-radius counts. Optional: a screen without them still gets the tools. */
  reloadUsage?: () => void;
  usage?: StructureUsage | null;
  /** The value this screen currently holds, so a rename or removal can be followed. */
  selected?: string | null;
  onSelected?: (next: string | null) => void;
  /**
   * `intakeMeta.labels` and `intakeMeta.can.builtinLabels` (migration 025). Threaded here
   * rather than spread in at every call site: there are three screens × three built-in
   * pickers, and nine hand-written spreads is exactly how one screen ends up without the
   * pencil — the copy-drift this module was extracted to prevent in the first place.
   */
  labels?: { brand?: string; category?: string; location?: string } | undefined;
  canRenameHeading?: boolean | undefined;
}

/**
 * THE THREE BUILT-IN HEADINGS (migration 025) — maintainer, 1 Aug 2026: "there should not
 * be a difference between the brand drop down, the category drop down, the location drop
 * down, or a field drop down."
 *
 * ⚠️ RENAME ONLY, NO DELETE, and that is the one place they still differ from a
 * user-defined field — necessarily. `items.brand`, `categories` and `locations` exist
 * whatever anyone calls them, so there is nothing to delete. `OptionManage.self.remove` is
 * optional for exactly this, and the ✕ is then absent rather than present-and-refusing.
 *
 * `label` is what the picker shows: the tenant's override when they have set one, else the
 * caller's translated default. The server stores only the override — it never invents an
 * English word, because the app runs in NL and EN.
 */
/**
 * ⚠️ TOLERANT READ ACROSS THE DEPLOY WINDOW — the 0.1.88 lesson, applied before it cost
 * anything this time.
 *
 * `intakeMeta.can` arrives with migrations 024/025. The web bundle reaches Cloudflare in
 * SECONDS and the API reaches Fly MINUTES later, so on every single release there is a
 * window where a new client asks an older server for a field it does not send yet — and
 * `meta.can.brands` on an object with no `can` is a TypeError that unmounts the whole
 * screen. That is exactly how one flag made an item permanently unopenable in 0.1.88.
 *
 * The parameter type is structurally OPTIONAL so `?.` is honest rather than dead code: the
 * current server always sends it, an older one does not, and this reader is written for
 * both. Absent reads as "capability off", which degrades to the pre-024 behaviour — the
 * ＋ is hidden and the heading is not renameable, which is exactly right for a server that
 * cannot do either.
 *
 * Its own test feeds `{}` for precisely this case.
 */
export function metaCan(
  meta: { can?: { brands?: boolean; builtinLabels?: boolean } | undefined },
  key: 'brands' | 'builtinLabels',
): boolean {
  return meta.can?.[key] === true;
}

/** The three built-in defaults, in one place so three screens cannot drift apart. */
const BUILTIN_DEFAULT = { brand: 'Brand', category: 'Category', location: 'Location' } as const;

/**
 * What a built-in picker's heading should SAY: the tenant's override when they have set
 * one, else the default.
 *
 * ⚠️ THE DEFAULT IS A LITERAL, NOT A TRANSLATION KEY, because these three headings have
 * always been literals at their call sites and the app opens in English (0.1.69 deleted the
 * browser sniff). Routing them through `t()` here would be a translation change smuggled
 * into a rename feature, and it would silently alter three screens for the Dutch reader
 * without anyone asking for it.
 */
export function builtinLabel(
  labels: { brand?: string; category?: string; location?: string } | undefined,
  key: 'brand' | 'category' | 'location',
): string {
  const override = labels?.[key];
  return override !== undefined && override.trim() !== '' ? override : BUILTIN_DEFAULT[key];
}

export function builtinSelf(
  key: 'brand' | 'category' | 'location',
  label: string,
  setMeta: (meta: never) => void,
  /** Absent = this database has not had 025, so the heading is not renameable at all. */
  enabled: boolean,
): OptionManage['self'] {
  if (!enabled) return undefined;
  return {
    noun: 'heading',
    label,
    rename: async (next: string) => {
      const r = await call('renameBuiltinLabel', () => api.renameBuiltinLabel.mutate({ key, label: next }));
      if (!r.ok) return r.failure.message;
      (setMeta as (m: unknown) => void)(r.data.meta);
      return null;
    },
  };
}

/**
 * Categories: a tree. The server derives a child's path from its parent, so a path is
 * offered as a parent and never typed — letting anyone type "A/B" would let them invent an
 * orphan whose text claims a parent it does not have.
 */
export function categoryManage<T extends { categories: { path: string }[] }>(
  meta: T,
  wiring: Wiring<T>,
): OptionManage {
  const { setMeta, reloadUsage, usage, selected, onSelected, labels, canRenameHeading } = wiring;
  return {
    noun: 'category',
    self: builtinSelf('category', builtinLabel(labels, 'category'), setMeta, canRenameHeading === true),
    parentOptions: meta.categories.map((c) => c.path),
    radiusFor: (path) => {
      const used = usage?.categories.find((c) => c.path === path);
      if (used === undefined) {
        // Not loaded, or a category the usage read did not cover. Saying "no item uses it"
        // here would be an invented reassurance before a silent blanking.
        return 'Items using it will lose their category (the count could not be read just now)';
      }
      if (used.childCount > 0) {
        // The database RESTRICTs this one, so the refusal is certain — say it before the
        // confirm rather than surfacing a classified error after.
        return `It has ${String(used.childCount)} sub-categor${used.childCount === 1 ? 'y' : 'ies'} and cannot be deleted until they are gone`;
      }
      return used.itemCount > 0
        ? `${String(used.itemCount)} item${used.itemCount === 1 ? '' : 's'} will lose their category (the items themselves are kept)`
        : 'No item uses it';
    },
    rename: async (path, next) => {
      const r = await call('renameCategory', () => api.renameCategory.mutate({ path, name: next }));
      if (!r.ok) return r.failure.message;
      reloadUsage?.();
      // `renameCategory` and `createCategory` answer with { …, meta }; `delete` answers
      // with the meta alone. Unwrapped per procedure rather than cast.
      setMeta(r.data.meta as unknown as T);
      // The screen's own selection follows the rename, or it would keep naming a path that
      // no longer exists and saving would clear the category.
      if (selected === path) {
        const renamed = (r.data.meta as unknown as T).categories.find(
          (c) => c.path.endsWith(`/${next}`) || c.path === next,
        );
        onSelected?.(renamed?.path ?? null);
      }
      return null;
    },
    remove: async (path) => {
      const r = await call('deleteCategory', () => api.deleteCategory.mutate({ path }));
      if (!r.ok) return r.failure.message;
      reloadUsage?.();
      setMeta(r.data as unknown as T);
      if (selected === path) onSelected?.(null);
      return null;
    },
    add: async (name, parentPath) => {
      // The parent comes from the add row's own select, never inferred from what happens to
      // be selected — that would be a default masquerading as a choice.
      const r = await call('createCategory', () => api.createCategory.mutate({ name, parentPath }));
      if (!r.ok) return r.failure.message;
      reloadUsage?.();
      setMeta(r.data.meta as unknown as T);
      return null;
    },
  };
}

/** Locations: flat. No `parentOptions`, so the add row never offers a parent. */
export function locationManage<T extends { locations: { name: string }[] }>(
  meta: T,
  wiring: Wiring<T>,
): OptionManage {
  const { setMeta, reloadUsage, usage, selected, onSelected, labels, canRenameHeading } = wiring;
  void meta;
  return {
    noun: 'location',
    self: builtinSelf('location', builtinLabel(labels, 'location'), setMeta, canRenameHeading === true),
    radiusFor: (name) => {
      const used = usage?.locations.find((l) => l.name === name);
      if (used === undefined) {
        return 'Items stored there will lose their location (the count could not be read just now)';
      }
      return used.itemCount > 0
        ? `${String(used.itemCount)} item${used.itemCount === 1 ? '' : 's'} will lose their location (the items themselves are kept)`
        : 'No item is stored there';
    },
    rename: async (name, next) => {
      const r = await call('renameLocation', () => api.renameLocation.mutate({ name, newName: next }));
      if (!r.ok) return r.failure.message;
      reloadUsage?.();
      setMeta(r.data.meta as unknown as T);
      // Follow the rename, or the chip keeps naming a location that is gone.
      if (selected === name) onSelected?.(next);
      return null;
    },
    remove: async (name) => {
      const r = await call('deleteLocation', () => api.deleteLocation.mutate({ name }));
      if (!r.ok) return r.failure.message;
      reloadUsage?.();
      setMeta(r.data as unknown as T);
      if (selected === name) onSelected?.(null);
      return null;
    },
    add: async (name) => {
      const r = await call('createLocation', () => api.createLocation.mutate({ name }));
      if (!r.ok) return r.failure.message;
      reloadUsage?.();
      setMeta(r.data.meta as unknown as T);
      return null;
    },
  };
}

/**
 * A FIELD'S OPTIONS, managed inside that field's own dropdown.
 *
 * `radiusFor` speaks about the OPTION, not the field: removing a suggestion leaves every
 * stored value alone. Saying "12 items will lose this" here would be a lie borrowed from
 * the field-deletion confirm, which is a different act with a genuinely destructive radius.
 */
export function fieldOptionManage<T>(
  fieldKey: string,
  setMeta: (meta: T) => void,
  /**
   * Present = the pencil also renames and deletes THE FIELD, as a separate row below the
   * options (maintainer, 31 Jul 2026). Absent = options only.
   *
   * `usage` is the count behind the delete confirm. Undefined means "not read", which the
   * wording says out loud — deleting a field destroys every stored value, so an invented
   * "no item carries it" would be the most expensive reassurance in the app.
   */
  self?: {
    label: string;
    usage?: { valueCount: number; itemCount: number } | undefined;
    /** Close the dropdown: it belonged to a field that no longer exists. */
    onGone?: () => void;
    reloadUsage?: () => void;
  },
): OptionManage {
  return {
    ...(self === undefined
      ? {}
      : {
          self: {
            noun: 'field',
            label: self.label,
            radius:
              self.usage === undefined
                ? 'Its stored values on every item are deleted with it (the count could not be read just now)'
                : self.usage.valueCount > 0
                  ? `${String(self.usage.valueCount)} value${self.usage.valueCount === 1 ? '' : 's'} on ${String(self.usage.itemCount)} item${self.usage.itemCount === 1 ? '' : 's'} are deleted with it`
                  : 'No item carries a value for it',
            rename: async (next: string) => {
              const r = await call('renameFieldDef', () =>
                api.renameFieldDef.mutate({ key: fieldKey, label: next }),
              );
              if (!r.ok) return r.failure.message;
              setMeta(r.data as unknown as T);
              self.reloadUsage?.();
              return null;
            },
            remove: async () => {
              const r = await call('deleteFieldDef', () => api.deleteFieldDef.mutate({ key: fieldKey }));
              if (!r.ok) return r.failure.message;
              setMeta(r.data as unknown as T);
              self.reloadUsage?.();
              self.onGone?.();
              return null;
            },
          },
        }),
    noun: 'option',
    radiusFor: () => 'It stops being suggested here. Items already carrying this value keep it',
    rename: async (value, next) => {
      /**
       * There is no rename-option procedure: options are suggestions, not an enum, so a
       * rename is an add plus a remove. Done in that order — if the add is refused nothing
       * is lost, whereas removing first would drop the suggestion and leave nothing in its
       * place.
       */
      const added = await call('addFieldOption', () => api.addFieldOption.mutate({ key: fieldKey, value: next }));
      if (!added.ok) return added.failure.message;
      const removed = await call('removeFieldOption', () => api.removeFieldOption.mutate({ key: fieldKey, value }));
      if (!removed.ok) return removed.failure.message;
      setMeta(removed.data as unknown as T);
      return null;
    },
    remove: async (value) => {
      const r = await call('removeFieldOption', () => api.removeFieldOption.mutate({ key: fieldKey, value }));
      if (!r.ok) return r.failure.message;
      setMeta(r.data as unknown as T);
      return null;
    },
    add: async (value) => {
      const r = await call('addFieldOption', () => api.addFieldOption.mutate({ key: fieldKey, value }));
      if (!r.ok) return r.failure.message;
      setMeta(r.data as unknown as T);
      return null;
    },
  };
}

/**
 * BRANDS — the one picker whose tools rewrite ITEMS rather than a table.
 *
 * `intakeMeta` derives the brand list with `SELECT DISTINCT brand FROM items`, so there is
 * no brand row to edit. A rename is an UPDATE across every item carrying the old spelling,
 * and a delete blanks the column on all of them. Built 31 Jul 2026 on the maintainer's
 * instruction ("build the brand merge") because the Cheqroom import left the same house
 * under several spellings and nothing could join them: the picker takes free entry, so a
 * corrected spelling only ever ADDED a third brand.
 *
 * ⚠️ EVERY WORD OF THE CONFIRM COPY IS LOAD-BEARING, because this is the only picker where
 * one press can touch dozens of garments:
 *   * a rename onto a name that already exists is a MERGE, and says so before it runs —
 *     "renamed" and "merged into" mean different things to whoever presses it;
 *   * `radiusFor` names the item count, so ✕ can never look like tidying a short list;
 *   * ＋ IS ABSENT. There is nothing to create: a brand exists the moment an item carries
 *     it, and the picker already takes free entry. An add row here would write nothing and
 *     look like it had.
 */
export function brandManage<T>(wiring: {
  setMeta: (meta: T) => void;
  usage?: StructureUsage | null;
  reloadUsage?: () => void;
  selected?: string | null;
  onSelected?: (next: string | null) => void;
  /** Said out loud after the write — what MOVED, not what was predicted. */
  onOutcome?: (line: string) => void;
  /** `intakeMeta.can.brands` — whether migration 024 is applied. Absent/false means ＋
   * refuses with the pre-024 sentence instead of calling a procedure that cannot work. */
  canCreate?: boolean;
  labels?: { brand?: string; category?: string; location?: string } | undefined;
  canRenameHeading?: boolean | undefined;
  /**
   * The merge confirm (rename-onto-existing) — INJECTED, because this factory is React-free and
   * cannot host a hook (S2-c). The React consumer passes `ask` from `useConfirm()`; there is no
   * `window.confirm` fallback (the whole point). Returns true to proceed.
   */
  confirm: (request: { title: string; body: string; confirmLabel: string }) => Promise<boolean>;
}): OptionManage {
  const { setMeta, usage, reloadUsage, selected, onSelected, onOutcome, canCreate, labels, canRenameHeading, confirm } =
    wiring;
  const countOf = (name: string): number | undefined => usage?.brands.find((b) => b.name === name)?.itemCount;
  return {
    noun: 'brand',
    self: builtinSelf('brand', builtinLabel(labels, 'brand'), setMeta, canRenameHeading === true),
    radiusFor: (name) => {
      const n = countOf(name);
      if (n === undefined) {
        return 'Every item carrying it loses its brand (the count could not be read just now)';
      }
      return n > 0
        ? `${String(n)} item${n === 1 ? '' : 's'} will lose their brand (the items themselves are kept)`
        : 'No item carries it';
    },
    rename: async (name, next) => {
      /**
       * THE MERGE WARNING, ASKED BEFORE THE WRITE. Renaming onto an existing brand joins
       * two sets of items permanently and cannot be undone from the app, so it is not
       * something to discover from the confirmation line afterwards.
       */
      const target = countOf(next);
      if (target !== undefined && target > 0) {
        const mine = countOf(name);
        const ok = await confirm({
          title: `Merge “${name}” into “${next}”?`,
          body:
            `${mine === undefined ? 'Every item' : `${String(mine)} item${mine === 1 ? '' : 's'}`} ` +
            `carrying “${name}” will be changed to “${next}”, joining the ${String(target)} already there. ` +
            'This cannot be undone from the app.',
          confirmLabel: 'Merge',
        });
        if (!ok) return null;
      }
      const r = await call('renameBrand', () => api.renameBrand.mutate({ from: name, to: next }));
      if (!r.ok) return r.failure.message;
      setMeta(r.data.meta as unknown as T);
      reloadUsage?.();
      if (selected === name) onSelected?.(next);
      onOutcome?.(
        r.data.merged
          ? `${String(r.data.affected)} item${r.data.affected === 1 ? '' : 's'} merged into “${next}” ✓`
          : `${String(r.data.affected)} item${r.data.affected === 1 ? '' : 's'} renamed to “${next}” ✓`,
      );
      return null;
    },
    remove: async (name) => {
      const r = await call('deleteBrand', () => api.deleteBrand.mutate({ name }));
      if (!r.ok) return r.failure.message;
      setMeta(r.data.meta as unknown as T);
      reloadUsage?.();
      if (selected === name) onSelected?.(null);
      onOutcome?.(`${String(r.data.affected)} item${r.data.affected === 1 ? '' : 's'} no longer carry “${name}” ✓`);
      return null;
    },
    /**
     * ⚠️ ＋ NOW ADDS A BRAND (migration 024) — maintainer, 1 Aug 2026, overruling the note
     * at the top of this function: "make it so i can add a brand even when no items carry
     * that brand yet, so that when I have an item with the wrong brand i can add the right
     * brand without having to fully add that item as new again."
     *
     * The old reasoning is kept above rather than deleted, because it was CORRECT about the
     * schema as it stood — there was genuinely nowhere to put a brand nothing carried, so
     * an add row would have written nothing and looked like it had. 024 is what changes the
     * fact, not the argument. The workflow it missed is correcting a misspelling on an
     * existing garment, where the right spelling has to exist before you can pick it.
     *
     * ⚠️ WITHOUT 024 IT STILL REFUSES, with the same sentence as before. `canCreate` is the
     * capability read, threaded from `intakeMeta.can.brands` — the screen also hides the ＋
     * entirely in that case, so this is the second line of defence rather than the first.
     */
    add: async (name: string) => {
      if (canCreate !== true) {
        return 'A brand exists as soon as an item carries it — type it in the box above instead.';
      }
      const r = await call('createBrand', () => api.createBrand.mutate({ name }));
      if (!r.ok) return r.failure.message;
      setMeta(r.data.meta as unknown as T);
      reloadUsage?.();
      onOutcome?.(`“${r.data.name}” added to the brand list ✓`);
      return null;
    },
  };
}
